<template>
  <div class="space-y-6">
    <!-- Empty state when no data -->
    <div v-if="!appointments || appointments.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
      <BaseIcon icon="calendar-alt" class="text-4xl text-gray-400 mb-4" />
      <h3 class="text-lg font-semibold text-gray-700 mb-2">Inga möten tillgängliga</h3>
      <p class="text-gray-600">Hämta data från Upsales eller ladda upp en mötesfil.</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900">
              Möten {{ filterYear === 'Alla' ? '' : filterYear }}
            </h2>
            <p class="text-gray-600 mt-1">
              {{ currentYearAppointments.length }} möten totalt · {{ filtered.length }} visas
              <span v-if="dateField !== 'Mötesdatum'" class="text-blue-600 ml-1">
                (filtrerat på {{ dateField.toLowerCase() }})
              </span>
            </p>
          </div>
          <BaseIcon icon="calendar-alt" class="text-2xl text-blue-600" />
        </div>
      </div>

      <!-- No data for selected year -->
      <div v-if="currentYearAppointments.length === 0 && filterYear !== 'Alla'" class="bg-white rounded-lg shadow p-8 text-center">
        <BaseIcon icon="calendar-alt" class="text-4xl text-gray-400 mb-4" />
        <h3 class="text-lg font-semibold text-gray-700 mb-2">Inga möten för {{ filterYear }}</h3>
        <p class="text-gray-600">Totalt {{ appointments.length }} möten inlästa, men inga för valt år.</p>
      </div>

      <template v-else>
        <!-- KPI Summary -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-xs text-gray-500 uppercase tracking-wide">Totalt möten</p>
            <p class="text-2xl font-bold text-gray-900 mt-1">{{ currentYearAppointments.length }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-xs text-gray-500 uppercase tracking-wide">Med utfall</p>
            <p class="text-2xl font-bold text-green-600 mt-1">{{ stats.withOutcome }}</p>
            <p class="text-xs text-gray-400 mt-0.5">{{ percent(stats.withOutcome) }}% ifyllda</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-xs text-gray-500 uppercase tracking-wide">Kopplade till affär</p>
            <p class="text-2xl font-bold text-blue-600 mt-1">{{ stats.withOpportunity }}</p>
            <p class="text-xs text-gray-400 mt-0.5">{{ percent(stats.withOpportunity) }}% kopplade</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-xs text-gray-500 uppercase tracking-wide">Saknar utfall</p>
            <p class="text-2xl font-bold text-orange-500 mt-1">
              {{ currentYearAppointments.length - stats.withOutcome }}
            </p>
            <p class="text-xs text-gray-400 mt-0.5">behöver uppföljning</p>
          </div>
        </div>

        <!-- Meeting Type Breakdown -->
        <div v-if="Object.keys(stats.typeBreakdown).length > 0" class="bg-white rounded-lg shadow p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <BaseIcon icon="tag" /> Fördelning per mötestyp
          </h3>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="entry in sortedTypeBreakdown"
              :key="entry.type"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all"
              :class="[
                typeColor(entry.type),
                filterType === entry.type ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-80 hover:opacity-100'
              ]"
              @click="filterType = filterType === entry.type ? 'Alla' : entry.type"
            >
              {{ entry.type }}
              <span class="font-bold">{{ entry.count }}</span>
            </button>
          </div>
        </div>

        <!-- Registrations per seller chart -->
        <div v-if="registrationsBySeller.length > 0" class="bg-white rounded-lg shadow p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <BaseIcon icon="chart-bar" /> Möten registrerade i Upsales per säljare
          </h3>
          <p class="text-xs text-gray-500 mb-4">
            Antal möten som varje säljare har registrerat (baserat på bokningsdatum)
          </p>
          <div style="height: 360px;">
            <canvas ref="regChart"></canvas>
          </div>
          <div class="mt-4 text-xs text-gray-500 text-center">
            Totalt: {{ totalRegistrations }} registrerade möten
          </div>
        </div>

        <!-- Time Filters -->
        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <BaseIcon icon="clock" /> Tidsfilter
          </h3>
          <div class="flex flex-wrap gap-4 items-end">
            <div>
              <label class="block text-xs text-gray-500 mb-1">Filtrera på</label>
              <select v-model="dateField" @change="resetTimeFilters" class="border border-gray-300 rounded px-3 py-1.5 text-sm font-medium min-w-[140px]">
                <option value="Mötesdatum">Mötesdatum</option>
                <option value="Bokningsdatum">Bokningsdatum</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">År</label>
              <select v-model="filterYear" @change="onYearChange" class="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[100px]">
                <option value="Alla">Alla år</option>
                <option v-for="y in availableYears" :key="y" :value="String(y)">{{ y }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Månad</label>
              <select v-model="filterMonth" :disabled="filterYear === 'Alla'" @change="filterWeek = 'Alla'" class="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[120px]">
                <option value="Alla">Alla månader</option>
                <option v-for="m in availableMonths" :key="m" :value="String(m)">{{ monthName(m) }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Vecka</label>
              <select v-model="filterWeek" :disabled="filterYear === 'Alla'" class="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[100px]">
                <option value="Alla">Alla veckor</option>
                <option v-for="w in availableWeeks" :key="w" :value="String(w)">Vecka {{ w }}</option>
              </select>
            </div>
            <button v-if="hasTimeFilters" class="text-xs text-blue-600 hover:underline mt-1" @click="resetTimeFilters">
              Återställ tidsfilter
            </button>
          </div>
        </div>

        <!-- Other Filters -->
        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Filter</h3>
          <div class="flex flex-wrap gap-4 items-end">
            <div>
              <label class="block text-xs text-gray-500 mb-1">Mötestyp</label>
              <select v-model="filterType" class="border border-gray-300 rounded px-3 py-1.5 text-sm">
                <option v-for="t in meetingTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Säljare</label>
              <select v-model="filterSeller" class="border border-gray-300 rounded px-3 py-1.5 text-sm">
                <option v-for="s in sellers" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Utfall</label>
              <select v-model="filterOutcome" class="border border-gray-300 rounded px-3 py-1.5 text-sm">
                <option v-for="o in outcomes" :key="o" :value="o">{{ o }}</option>
              </select>
            </div>
            <button v-if="hasOtherFilters" class="text-xs text-blue-600 hover:underline mt-1" @click="resetOtherFilters">
              Rensa filter
            </button>
          </div>
        </div>

        <!-- Meetings table -->
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="w-8 p-3"></th>
                  <th class="p-3 text-left">Mötesdatum</th>
                  <th class="p-3 text-left">Bokningsdatum</th>
                  <th class="p-3 text-left">Mötestyp</th>
                  <th class="p-3 text-left">Beskrivning</th>
                  <th class="p-3 text-left">Företag</th>
                  <th class="p-3 text-left">Säljare</th>
                  <th class="p-3 text-left">Kontaktpersoner</th>
                  <th class="p-3 text-left">Plats</th>
                  <th class="p-3 text-left">Utfall</th>
                  <th class="p-3 text-left">Opportunity</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(a, idx) in filtered">
                  <tr :key="`row-${rowKey(a, idx)}`" class="cursor-pointer border-t hover:bg-blue-50" @click="toggleRow(rowKey(a, idx))">
                    <td class="pl-3">
                      <BaseIcon :icon="expandedRow === rowKey(a, idx) ? 'chevron-down' : 'chevron-right'" class="text-gray-400" />
                    </td>
                    <td class="p-3 font-medium">
                      <span class="inline-flex items-center gap-1.5"><BaseIcon icon="calendar-alt" class="text-gray-400" />{{ formatDate(a['Mötesdatum']) }}</span>
                    </td>
                    <td class="p-3 text-gray-600">
                      <span class="inline-flex items-center gap-1.5"><BaseIcon icon="clock" class="text-gray-400" />{{ formatDateShort(a['Bokningsdatum']) }}</span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Mötestyp']" :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', typeColor(a['Mötestyp'])]">
                        {{ a['Mötestyp'] }}
                      </span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                    <td class="p-3"><p class="truncate max-w-xs" :title="a['Beskrivning']">{{ a['Beskrivning'] || '–' }}</p></td>
                    <td class="p-3">
                      <span v-if="a['Företag']" class="inline-flex items-center gap-1.5"><BaseIcon icon="building" class="text-gray-400" /><span class="truncate max-w-[160px]" :title="a['Företag']">{{ a['Företag'] }}</span></span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Säljare']" class="inline-flex items-center gap-1.5"><BaseIcon icon="user" class="text-gray-400" /><span class="truncate max-w-[130px]" :title="a['Säljare']">{{ a['Säljare'] }}</span></span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Kontaktpersoner']" class="inline-flex items-center gap-1.5"><BaseIcon icon="users" class="text-gray-400" /><span class="truncate max-w-[130px]" :title="a['Kontaktpersoner']">{{ a['Kontaktpersoner'] }}</span></span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Plats']" class="inline-flex items-center gap-1"><BaseIcon icon="map-marker-alt" class="text-gray-400" /><span class="truncate max-w-[90px]" :title="a['Plats']">{{ a['Plats'] }}</span></span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Utfall']" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <BaseIcon icon="check-circle" /><span class="truncate max-w-[90px]" :title="a['Utfall']">{{ a['Utfall'] }}</span>
                      </span>
                      <span v-else class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600">
                        <BaseIcon icon="times-circle" />Saknas
                      </span>
                    </td>
                    <td class="p-3">
                      <span v-if="a['Opportunity ID']" class="inline-flex items-center gap-1 font-mono text-xs text-blue-700">
                        <BaseIcon icon="file-alt" class="text-blue-400" />#{{ a['Opportunity ID'] }}
                      </span>
                      <span v-else class="text-gray-300 text-xs">–</span>
                    </td>
                  </tr>
                  <tr v-if="expandedRow === rowKey(a, idx)" :key="`exp-${rowKey(a, idx)}`" class="bg-blue-50 border-t border-blue-100">
                    <td colspan="11" class="p-5">
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div class="space-y-2">
                          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1.5 mb-2">Mötesinfo</h4>
                          <div><span class="text-xs text-gray-500">ID:</span> <span class="font-mono text-xs">{{ a['ID'] }}</span></div>
                          <div><span class="text-xs text-gray-500">Mötesdatum:</span> {{ formatDate(a['Mötesdatum']) }}</div>
                          <div><span class="text-xs text-gray-500">Bokningsdatum:</span> {{ formatDate(a['Bokningsdatum']) }}</div>
                          <div><span class="text-xs text-gray-500">Mötestyp:</span> {{ a['Mötestyp'] || '–' }}</div>
                          <div><span class="text-xs text-gray-500">Plats:</span> {{ a['Plats'] || '–' }}</div>
                        </div>
                        <div class="space-y-2">
                          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1.5 mb-2">Parter</h4>
                          <div><span class="text-xs text-gray-500">Företag:</span> {{ a['Företag'] || '–' }}</div>
                          <div><span class="text-xs text-gray-500">Kontaktpersoner:</span> {{ a['Kontaktpersoner'] || '–' }}</div>
                          <div><span class="text-xs text-gray-500">Säljare:</span> {{ a['Säljare'] || '–' }}</div>
                        </div>
                        <div class="space-y-2">
                          <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1.5 mb-2">Resultat &amp; Kopplingar</h4>
                          <div><span class="text-xs text-gray-500">Utfall:</span> {{ a['Utfall'] || 'Saknas' }}</div>
                          <div><span class="text-xs text-gray-500">Opportunity ID:</span> <span v-if="a['Opportunity ID']" class="font-mono text-xs">#{{ a['Opportunity ID'] }}</span><span v-else>–</span></div>
                          <div v-if="a['Beskrivning']" class="mt-2">
                            <p class="text-xs text-gray-500 mb-1">Beskrivning</p>
                            <p class="text-sm text-gray-700 bg-white rounded p-2 border border-gray-200 whitespace-pre-wrap">{{ a['Beskrivning'] }}</p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-if="filtered.length === 0" class="p-8 text-center text-gray-500">
            Inga möten matchar de valda filtren.
          </div>
        </div>

        <p class="text-xs text-gray-400 text-center">
          💡 Mötesdatum = när mötet sker · Bokningsdatum = när det registrerades i Upsales · Klicka på en rad för detaljer
        </p>
      </template>
    </template>
  </div>
</template>

<script>
import Chart from "chart.js";

const MONTH_NAMES = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December",
];

function getISOWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

export default {
  name: "PipelineMeetings",
  props: {
    appointments: { type: Array, default: () => [] },
  },
  data() {
    return {
      expandedRow: null,
      filterType: "Alla",
      filterSeller: "Alla",
      filterOutcome: "Alla",
      dateField: "Mötesdatum",
      filterYear: String(new Date().getFullYear()),
      filterMonth: "Alla",
      filterWeek: "Alla",
      regChart: null,
    };
  },
  computed: {
    availableYears() {
      const years = new Set();
      this.appointments.forEach((a) => {
        const datum = a[this.dateField];
        if (datum) years.add(new Date(datum).getFullYear());
      });
      return Array.from(years).sort((a, b) => b - a);
    },
    yearFiltered() {
      return this.appointments
        .filter((a) => {
          const datum = a[this.dateField];
          if (!datum) return false;
          if (this.filterYear === "Alla") return true;
          return new Date(datum).getFullYear() === parseInt(this.filterYear, 10);
        })
        .sort((a, b) => new Date(b[this.dateField] || 0) - new Date(a[this.dateField] || 0));
    },
    availableMonths() {
      const months = new Set();
      this.yearFiltered.forEach((a) => {
        const datum = a[this.dateField];
        if (datum) months.add(new Date(datum).getMonth());
      });
      return Array.from(months).sort((a, b) => a - b);
    },
    availableWeeks() {
      const weeks = new Set();
      this.yearFiltered.forEach((a) => {
        const datum = a[this.dateField];
        if (!datum) return;
        const date = new Date(datum);
        if (this.filterMonth === "Alla" || date.getMonth() === parseInt(this.filterMonth, 10)) {
          weeks.add(getISOWeek(date));
        }
      });
      return Array.from(weeks).sort((a, b) => a - b);
    },
    currentYearAppointments() {
      return this.yearFiltered.filter((a) => {
        const datum = a[this.dateField];
        if (!datum) return false;
        const date = new Date(datum);
        if (this.filterMonth !== "Alla" && date.getMonth() !== parseInt(this.filterMonth, 10)) return false;
        if (this.filterWeek !== "Alla" && getISOWeek(date) !== parseInt(this.filterWeek, 10)) return false;
        return true;
      });
    },
    meetingTypes() {
      const set = new Set(this.currentYearAppointments.map((a) => a["Mötestyp"]).filter(Boolean));
      return ["Alla", ...Array.from(set).sort()];
    },
    sellers() {
      const set = new Set();
      this.currentYearAppointments.forEach((a) => {
        const s = a["Säljare"];
        if (s) s.split(", ").forEach((name) => set.add(name.trim()));
      });
      return ["Alla", ...Array.from(set).sort()];
    },
    outcomes() {
      const set = new Set(this.currentYearAppointments.map((a) => a["Utfall"]).filter(Boolean));
      return ["Alla", "Saknas", ...Array.from(set).sort()];
    },
    filtered() {
      return this.currentYearAppointments.filter((a) => {
        if (this.filterType !== "Alla" && a["Mötestyp"] !== this.filterType) return false;
        if (this.filterSeller !== "Alla") {
          const sl = (a["Säljare"] || "").split(", ").map((s) => s.trim());
          if (!sl.includes(this.filterSeller)) return false;
        }
        if (this.filterOutcome === "Saknas" && a["Utfall"]) return false;
        if (this.filterOutcome !== "Alla" && this.filterOutcome !== "Saknas" && a["Utfall"] !== this.filterOutcome) return false;
        return true;
      });
    },
    stats() {
      const withOutcome = this.currentYearAppointments.filter((a) => a["Utfall"]).length;
      const withOpportunity = this.currentYearAppointments.filter((a) => a["Opportunity ID"]).length;
      const typeBreakdown = {};
      this.currentYearAppointments.forEach((a) => {
        const t = a["Mötestyp"] || "Okänd";
        typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
      });
      return { withOutcome, withOpportunity, typeBreakdown };
    },
    sortedTypeBreakdown() {
      return Object.entries(this.stats.typeBreakdown)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => ({ type, count }));
    },
    registrationsBySeller() {
      const map = {};
      this.currentYearAppointments.forEach((a) => {
        const s = a["Säljare"];
        if (s) s.split(", ").forEach((name) => {
          const n = name.trim();
          if (n) map[n] = (map[n] || 0) + 1;
        });
      });
      return Object.entries(map).map(([name, antal]) => ({ name, antal })).sort((a, b) => b.antal - a.antal);
    },
    totalRegistrations() {
      return this.registrationsBySeller.reduce((s, x) => s + x.antal, 0);
    },
    hasTimeFilters() {
      return this.dateField !== "Mötesdatum"
        || this.filterYear !== String(new Date().getFullYear())
        || this.filterMonth !== "Alla"
        || this.filterWeek !== "Alla";
    },
    hasOtherFilters() {
      return this.filterType !== "Alla" || this.filterSeller !== "Alla" || this.filterOutcome !== "Alla";
    },
  },
  watch: {
    registrationsBySeller: {
      handler() { this.$nextTick(() => this.renderRegChart()); },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => this.renderRegChart());
  },
  beforeDestroy() {
    if (this.regChart) { this.regChart.destroy(); this.regChart = null; }
  },
  methods: {
    monthName(m) { return MONTH_NAMES[m]; },
    percent(n) {
      return this.currentYearAppointments.length === 0 ? 0 : Math.round((n / this.currentYearAppointments.length) * 100);
    },
    typeColor(type) {
      const map = {
        "Kundmöte": "bg-blue-100 text-blue-800",
        "Demo": "bg-purple-100 text-purple-800",
        "Uppföljning": "bg-yellow-100 text-yellow-800",
        "Telefonsamtal": "bg-green-100 text-green-800",
        "Förstamöte": "bg-blue-100 text-blue-800",
      };
      return map[type] || "bg-gray-100 text-gray-700";
    },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });
    },
    formatDateShort(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("sv-SE", { month: "short", day: "numeric" });
    },
    rowKey(a, idx) { return String(a["ID"] || `idx-${idx}`); },
    toggleRow(id) { this.expandedRow = this.expandedRow === id ? null : id; },
    onYearChange() { this.filterMonth = "Alla"; this.filterWeek = "Alla"; },
    resetTimeFilters() {
      this.dateField = "Mötesdatum";
      this.filterYear = String(new Date().getFullYear());
      this.filterMonth = "Alla";
      this.filterWeek = "Alla";
    },
    resetOtherFilters() {
      this.filterType = "Alla";
      this.filterSeller = "Alla";
      this.filterOutcome = "Alla";
    },
    renderRegChart() {
      if (this.regChart) { this.regChart.destroy(); this.regChart = null; }
      const el = this.$refs.regChart;
      if (!el || this.registrationsBySeller.length === 0) return;
      this.regChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.registrationsBySeller.map((r) => r.name),
          datasets: [{
            label: "Antal möten",
            data: this.registrationsBySeller.map((r) => r.antal),
            backgroundColor: "#3273DC",
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } }],
            yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
          },
          tooltips: {
            callbacks: { label: (item) => `${item.yLabel} möten` },
          },
        },
      });
    },
  },
};
</script>
